import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard, Users, Shield, Settings, CalendarDays,
  Clock, FileBarChart, Ticket, Menu, LogOut, ChevronDown, ShoppingCart
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', label: 'Panel', icon: LayoutDashboard, perm: null },
  { to: '/users', label: 'Usuarios', icon: Users, perm: 'users.read' },
  { to: '/roles-permissions', label: 'Roles y Permisos', icon: Shield, perm: 'roles.read' },
  { to: '/bar-settings', label: 'Config. Bar', icon: Settings, perm: 'bar_settings.update' },
  { to: '/schedule', label: 'Horarios', icon: CalendarDays, perm: null },
  { to: '/hours', label: 'Mis Horas', icon: Clock, perm: null },
  { to: '/reports', label: 'Informes', icon: FileBarChart, perm: 'reports.read_all' },
  { to: '/tickets', label: 'Tickets', icon: Ticket, perm: 'tickets.read' },
  { to: '/orders', label: 'Pedidos', icon: ShoppingCart, perm: 'orders.read' },
];

function SidebarNav({ onNav }) {
  const { hasPermission } = useAuth();
  const filtered = navItems.filter(item => {
    if (item.perm && !hasPermission(item.perm)) return false;
    if (item.permNot && hasPermission(item.permNot)) return false;
    return true;
  });

  return (
    <nav className="flex flex-col gap-1 px-3" data-testid="sidebar-nav">
      {filtered.map(item => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNav}
          data-testid={`nav-link-${item.to.slice(1)}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5'
            }`
          }
        >
          <item.icon className="w-4 h-4 shrink-0" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 glass-sidebar shrink-0" data-testid="desktop-sidebar">
        <div className="p-5 border-b border-white/5">
          <h1 className="font-heading text-xl font-bold text-zinc-50 tracking-tight">
            La <span className="text-amber-500">Finka</span>
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">Sistema de Gestion</p>
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <SidebarNav />
        </div>
        <div className="p-4 border-t border-white/5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="user-menu-trigger">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 text-sm font-semibold">
                  {user?.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-zinc-200 truncate">{user?.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-zinc-950 border-white/10">
              <DropdownMenuItem className="text-zinc-400 text-xs cursor-default focus:bg-transparent">
                {user?.groups_data?.map(g => g.name).join(', ')}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-400 cursor-pointer" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile header + content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between p-4 border-b border-white/5 glass-sidebar" data-testid="mobile-header">
          <h1 className="font-heading text-lg font-bold text-zinc-50">
            La <span className="text-amber-500">Finka</span>
          </h1>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-zinc-950 border-white/5 p-0">
              <SheetTitle className="sr-only">Menu de navegacion</SheetTitle>
              <div className="p-5 border-b border-white/5">
                <h2 className="font-heading text-lg font-bold text-zinc-50">
                  La <span className="text-amber-500">Finka</span>
                </h2>
              </div>
              <div className="py-4">
                <SidebarNav onNav={() => setOpen(false)} />
              </div>
              <div className="mt-auto p-4 border-t border-white/5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 text-sm font-semibold">
                    {user?.name?.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-200 truncate">{user?.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                  </div>
                </div>
                <Button variant="ghost" className="w-full justify-start text-red-400 hover:text-red-400 hover:bg-red-500/10" onClick={handleLogout} data-testid="mobile-logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesion
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
